/** Average listening speed for synthesized speech at 1x, in words per
 * minute — deliberately lower than `readingTime.ts`'s 200wpm silent-reading
 * figure (spoken delivery is slower than silent reading), and kept
 * completely separate: this widget's duration is computed from the actual
 * spoken segments, never reused from `readingTime.ts`. */
export const LISTENING_WORDS_PER_MINUTE = 160

interface WordsLike {
  words: number
  id?: string
}

/** Measured duration (seconds, at 1x) per segment id — known only for
 * engines that synthesize audio ahead of time. */
export type KnownDurations = ReadonlyMap<string, number>

/** Seconds a segment of `words` words takes to speak at `rate` (1 = normal
 * speed, 2 = twice as fast, …). */
export function segmentSeconds(words: number, rate: number): number {
  if (rate <= 0) return 0
  return (words / LISTENING_WORDS_PER_MINUTE / rate) * 60
}

/** Seconds `segment` takes at `rate`: its real (measured) duration when
 * known, otherwise the word-count estimate. */
export function segmentDuration(
  segment: WordsLike,
  rate: number,
  knownDurations?: KnownDurations,
): number {
  const known = segment.id === undefined ? undefined : knownDurations?.get(segment.id)
  if (known === undefined) return segmentSeconds(segment.words, rate)
  return rate <= 0 ? 0 : known / rate
}

/** Total duration (seconds) of every segment at `rate` — measured where
 * known, estimated elsewhere. */
export function totalSeconds(
  segments: readonly WordsLike[],
  rate: number,
  knownDurations?: KnownDurations,
): number {
  return segments.reduce(
    (total, segment) => total + segmentDuration(segment, rate, knownDurations),
    0,
  )
}

/**
 * Elapsed playback time (seconds): every segment before `segmentIndex` counts
 * in full, plus the current segment's estimated progress — `(now -
 * startedAt)` clamped to 95% of its own duration, so the displayed time
 * never reaches (and then overshoots) the segment's end before `onend`
 * actually fires. `now`/`startedAt` are `Date.now()`-style millisecond
 * timestamps.
 */
export function elapsedSeconds({
  segments,
  segmentIndex,
  rate,
  now,
  startedAt,
  knownDurations,
  currentProgress,
}: {
  segments: readonly WordsLike[]
  segmentIndex: number
  rate: number
  now: number
  startedAt: number
  knownDurations?: KnownDurations
  /** Exact fraction (`0..1`) of the current segment already played, when the
   * engine can report it — used instead of the wall-clock estimate. */
  currentProgress?: number | null
}): number {
  let completed = 0
  const lastCompletedIndex = Math.min(segmentIndex, segments.length)
  for (let position = 0; position < lastCompletedIndex; position++) {
    completed += segmentDuration(segments[position], rate, knownDurations)
  }

  const current = segments[segmentIndex]
  if (!current) return completed

  const currentDuration = segmentDuration(current, rate, knownDurations)
  if (currentDuration <= 0) return completed

  if (currentProgress !== undefined && currentProgress !== null) {
    return completed + Math.min(1, Math.max(0, currentProgress)) * currentDuration
  }
  const progress = Math.min(0.95, Math.max(0, (now - startedAt) / 1000 / currentDuration))
  return completed + progress * currentDuration
}

/** Index of the segment covering word offset `word`, given each segment's
 * `startWord`. Segments are contiguous and ordered, so this is a simple
 * linear scan; exposed mainly for tests and for a future TOC-sync feature. */
export function segmentIndexAtWord(
  segments: readonly { startWord: number }[],
  word: number,
): number {
  let matchIndex = 0
  for (let position = 0; position < segments.length; position++) {
    if (segments[position].startWord <= word) {
      matchIndex = position
    } else {
      break
    }
  }
  return matchIndex
}

/** Formats a duration in seconds as a `m:ss` clock string (e.g. `72` → `1:12`). */
export function formatClock(totalSecondsValue: number): string {
  const safeSeconds = Math.max(0, Math.round(totalSecondsValue))
  const minutes = Math.floor(safeSeconds / 60)
  const seconds = safeSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
